import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import Handlebars from "handlebars";
import { fetchApi } from "../actions/helpers";
import mapValues from "lodash/mapValues";
import objectValues from "lodash/values";
import merge from "lodash/merge";
import styles from "./EmailModule.module.css";

// Stable singleton context for if_eq helper - replicates CommonJS module.exports behavior
// In CommonJS, arrow function 'this' = module.exports (a stable {}), but in ES modules 'this' = undefined
// Passing undefined causes Handlebars to substitute nullContext, corrupting the depth stack
// and breaking ../ references inside nested block helpers like {{#if}}
const _ifEqContext = {};

Handlebars.registerHelper("if_eq", (context, options) => {
  if (context === options.hash.compare) {
    return options.fn(_ifEqContext);
  }
  return options.inverse(_ifEqContext);
});

Handlebars.registerHelper("assign", function (ctx, name, value, options) {
  // If ctx is undefined (e.g., ../this when there's no parent), use 'this' instead
  if (!ctx || typeof ctx !== 'object') {
    ctx = this;
  }
  
  var result = "";
  if (options && options.hash && options.hash["eval"]) {
    var fn = Function.apply(
      this,
      [].concat(Object.keys(ctx), "return " + value)
    );
    result = fn.apply(this, objectValues(ctx));
  } else {
    result = value;
  }
  
  ctx[name] = result;
  return "";
});

Handlebars.registerHelper("select_input", function (context, field) {
  if (!context || !context.global || context.global.__editor__ === undefined) {
    return new Handlebars.SafeString(`
          '"><span style="font-size:20px; color: red">select_input helper needs global context passed as 1st argument</span>
      `);
  }

  if (!context.global.__editor__) {
    return "";
  }

  return new Handlebars.SafeString(`
      onMouseOver="document.body.style.setProperty('--main-bg-color', window.parent.document.body.style.getPropertyValue('--main-bg-color'));this.style.boxShadow='0px 0px 4px 2px var(--main-bg-color)'"
      onMouseOut="this.style.boxShadow='none';"
      onClick="window.frameElement.parentElement.click();window.parent.document.querySelector('input[name=\\'${field}\\']').select(); return false;"
  `);
});

function fieldsDataToModuleData(fields) {
  if (!fields || typeof fields !== 'object') {
    return {};
  }
  const result = mapValues(fields, (value, key) => {
    if (value && value.type === "group") {
      return fieldsDataToModuleData(value);
    }
    return (value && (value.value || value.defaultValue)) || '';
  });
  
  return result;
}

const fixEncoding = (html) => {
  const mappings = [["&#38;", "&"]];
  return mappings.reduce((memo, mapping) => {
    const regex = new RegExp(mapping[0], "g");
    return memo.replace(regex, mapping[1]);
  }, html);
};

const translateEmail = (html, translations) => {
  for (var translationKey in translations) {
    var regex = new RegExp("\\|\\|" + translationKey + "\\|\\|", "g");
    html = html.replace(regex, translations[translationKey]);
  }
  return html;
};

const EmailModule = ({
  accountId,
  id,
  variation,
  translations,
  editor,
  template,
  data,
  globalOverrides,
  style,
  breakdown,
  selected,
  showMoveUp,
  showMoveDown,
  onSelect,
  onMoveUp,
  onMoveDown,
}) => {
  const [moduleData, setModuleData] = useState(null);
  const [mouseOver, setMouseOver] = useState(false);
  const [html, setHTML] = useState("");
  const [height, setHeight] = useState(0);
  const [templateError, setTemplateError] = useState(null);
  const templateFn = useRef();
  const iframe = useRef();
  const stableId = useRef("m" + Math.random().toString(36).substring(2, 15));

  const globalFields =
    ((moduleData && moduleData.EmailTemplate.fields) || {}).data || {};
  const globalData =
    moduleData &&
    Object.assign({}, fieldsDataToModuleData(globalFields), {
      subject: moduleData.name,
      __package__: false,
      __editor__: !!editor,
    });

  useEffect(() => {
    (async () => {
      if (id && accountId) {
        const res = await fetchApi(
          "/api/clients/" + accountId + "/email-modules/" + id
        );
        setModuleData(res.json);
      }
    })();
  }, [accountId, id]);

  useEffect(() => {
    if (moduleData) {
      try {
        const parentTemplate = Handlebars.compile(
          moduleData.EmailTemplate.template || ""
        );
        const moduleTemplate = Handlebars.compile(
          template || moduleData.template || ""
        );
        const moduleStyleTemplate = Handlebars.compile(
          style || moduleData.style || ""
        );

        templateFn.current = (data) => {
          return fixEncoding(
            parentTemplate(
              Object.assign({}, globalData, globalOverrides, {
                content: moduleTemplate(data),
                style: moduleStyleTemplate(data),
              })
            )
          );
        };
        setTemplateError(null);
      } catch (error) {
        setTemplateError(error.message);
        templateFn.current = null;
      }
    }
  }, [moduleData, editor, globalData, globalOverrides, template, style]);

  useEffect(() => {
    if (templateFn.current) {
      try {
        const moduleFields = fieldsDataToModuleData((moduleData.fields || {}).data || {});
        
        const templateData = merge(
          {},
          moduleFields,
          {
            global: Object.assign({}, globalData, data.global),
          },
          data,
          {
            __id__: stableId.current,
            __package__: false,
            __editor__: true,
          }
        );
        

        const builtHTML = templateFn.current(templateData);

        if (!translations) {
          setHTML(builtHTML);
        } else {
          var selectedVariation = variation || Object.keys(translations)[0];
          var selectedTranslation = translations[selectedVariation];
          setHTML(translateEmail(builtHTML, selectedTranslation));
        }
        setTemplateError(null);
      } catch (error) {
        setTemplateError(error.message);
      }
    }
  }, [templateFn, globalData, moduleData, data, translations, variation]);

  useEffect(() => {
    if (iframe.current && html) {
      try {
        const blob = new Blob([html], { type: "text/html" });
        const objectURL = URL.createObjectURL(blob);
        iframe.current.src = objectURL;
        
        return () => {
          URL.revokeObjectURL(objectURL);
        };
      } catch (error) {
        setTemplateError("Failed to render HTML: " + error.message);
      }
    }
  }, [iframe, html]);

  useLayoutEffect(() => {
    const timer = setInterval(() => {
      if (
        iframe.current &&
        iframe.current.contentDocument &&
        iframe.current.contentDocument.body
      ) {
        try {
          const doc = iframe.current.contentDocument;
          const body = doc.body;
          const html = doc.documentElement;
          
          // prevent unwanted whitespace being added by using body/html.scrollheight as iframe height
          const newHeight = body.scrollHeight || html.scrollHeight || 0;
          
          // Only update if height changed to prevent infinite loop
          setHeight(prevHeight => prevHeight !== newHeight ? newHeight : prevHeight);
        } catch (error) {
          // Iframe access error, possibly due to malformed HTML
          clearInterval(timer);
          setTemplateError("Preview error: Unable to render HTML. Check for malformed attributes or syntax errors.");
        }
      }
    }, 100);
    return () => {
      clearInterval(timer);
    };
  }, [iframe, html]);

  const onMouseOver = (e) => {
    setMouseOver(true);
  };

  const onMouseLeave = (e) => {
    setMouseOver(false);
  };

  const moveUpBtn = showMoveUp ? (
    <span className={styles.moveBtn} onClick={onMoveUp}>
      ⬆
    </span>
  ) : undefined;

  const moveDownBtn = showMoveDown ? (
    <span className={styles.moveBtn} onClick={onMoveDown}>
      ⬇
    </span>
  ) : undefined;

  if (templateError) {
    return (
      <div
        className={
          styles.module +
          (breakdown ? " " + styles.breakdown : "") +
          (selected ? " " + styles.selected : "")
        }
      >
        {moduleData && (
          <div className={styles.moduleName}>{moduleData.name}</div>
        )}
        <div className={styles.content} style={{ padding: "20px", color: "red", background: "#fff" }}>
          <strong>Template Error:</strong>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{templateError}</pre>
        </div>
      </div>
    );
  }

  if (!html) {
    return <div />;
  }

  return (
    <div
      className={
        styles.module +
        (breakdown ? " " + styles.breakdown : "") +
        (selected ? " " + styles.selected : "")
      }
      onClick={onSelect}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.moduleName}>
        {moduleData.name}
        <div className={styles.moveBtns}>
          {moveUpBtn}
          {moveDownBtn}
        </div>
      </div>
      <div className={styles.content}>
        <iframe
          title={moduleData.name}
          ref={iframe}
          width="100%"
          height={height}
          frameBorder="0"
          scrolling="no"
          src="about:blank"
        />
        {!mouseOver && (
          <div
            className={styles.overlay}
            style={{ background: "transparent" }}
          />
        )}
      </div>
    </div>
  );
};

export default EmailModule;
